import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'
import type {
  EventLayout,
  EventRenderContext,
  TimeGridEntity,
  TimeGridEvent,
  CardAppearance,
  GridDragAndDropAdapter,
  TimeGridDragItem,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { TimeGridRowEvents } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/TimeGridRowEvents'
import { SelectionOverlay } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/components/SelectionOverlay'
import { useCopiedSourceFeedback } from 'modules/shift_management/hooks/useClipboard'

interface Props<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop> {
  entity: TEntity
  slots: TimeSlot[]
  rowHeaderWidth: number
  minColumnWidth: number
  gridMinWidth: number
  paddingX: number
  paddingY: number
  cardLayout: {
    minHeight: number
    paddingX: number
    paddingY: number
    radius: number
  }
  showGridLines: boolean
  columnCount: number
  layouts: EventLayout<TEvent>[]
  rowHeader: ReactNode
  rowActions?: ReactNode
  eventRenderer: (event: TEvent, context: EventRenderContext<TDragDrop>) => ReactNode
  cardAppearance?: (event: TEvent) => CardAppearance
  dragDropHandlers?: TDragDrop
  dragAndDropAdapter?: GridDragAndDropAdapter<TEntity, TEvent>
  secondaryLayer?: ReactNode
  rowRef?: (node: HTMLDivElement | null) => void
  selectedCellSlots?: Set<number>
  isCellSelectable?: (slotIndex: number) => boolean
  disabledCellSlotIndexes?: Set<number>
  onCellSelected?: (
    slotIndex: number,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  isRowSelected?: boolean
  onRowHeaderSelected?: (modifiers: {
    shiftKey: boolean
    metaKey: boolean
    ctrlKey: boolean
  }) => void
  onHeaderDragStart?: (context: {
    axis: 'row' | 'column'
    index: number
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  }) => void
  rowIndex: number
  selectedEventIds?: Set<string>
  isSingleSelectedEventGlobally?: boolean
  onEventSelected?: (
    eventId: string,
    modifiers: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }
  ) => void
  onCopySelection?: () => void
  onAddShiftClick?: (slotIndex: number, anchorRect: DOMRect) => void
  ghostCellSlotIndexes?: Set<number> | null
  ghostAnchorSlotIndex?: number | null
  onGhostCardRef?: (node: HTMLDivElement | null) => void
}

const CELL_BOTTOM_PADDING_PX = 28

const TimeGridRow = <TEntity extends TimeGridEntity, TEvent extends TimeGridEvent, TDragDrop>({
  entity: _entity,
  slots,
  rowHeaderWidth,
  minColumnWidth,
  gridMinWidth,
  paddingX,
  paddingY,
  cardLayout,
  showGridLines,
  columnCount,
  layouts,
  rowHeader,
  rowActions,
  eventRenderer,
  cardAppearance,
  dragDropHandlers,
  dragAndDropAdapter,
  secondaryLayer,
  rowRef,
  selectedCellSlots,
  isCellSelectable,
  disabledCellSlotIndexes,
  onCellSelected,
  isRowSelected = false,
  onRowHeaderSelected,
  onHeaderDragStart,
  rowIndex,
  selectedEventIds,
  isSingleSelectedEventGlobally,

  onEventSelected,
  onCopySelection,
  onAddShiftClick,
  ghostCellSlotIndexes,
  ghostAnchorSlotIndex,
  onGhostCardRef,
}: Props<TEntity, TEvent, TDragDrop>) => {
  const [hoveredSlotIndex, setHoveredSlotIndex] = useState<number | null>(null)
  const [isInBottomPadding, setIsInBottomPadding] = useState(false)
  const [isHoveringAddButton, setIsHoveringAddButton] = useState(false)
  const [isRowHeaderHovered, setIsRowHeaderHovered] = useState(false)
  const { copiedCellSlots, copiedColumnIndexes, copiedRowId } = useCopiedSourceFeedback({
    entityId: _entity.id,
  })
  const isRowCopied = copiedRowId === _entity.id

  const rowBodyRef = useRef<HTMLDivElement | null>(null)

  const visibleLayouts = useMemo(() => {
    if (!disabledCellSlotIndexes?.size) return layouts

    return layouts.filter((layout) => {
      for (
        let slotIndex = layout.span.startIndex;
        slotIndex <= layout.span.endIndex;
        slotIndex += 1
      ) {
        if (disabledCellSlotIndexes.has(slotIndex)) return false
      }

      return true
    })
  }, [disabledCellSlotIndexes, layouts])

  const occupiedSlots = useMemo(() => {
    const occupied = new Set<number>()
    for (const layout of visibleLayouts) {
      for (let i = layout.span.startIndex; i <= layout.span.endIndex; i++) {
        occupied.add(i)
      }
    }
    return occupied
  }, [visibleLayouts])

  const eventsBySlot = useMemo(() => {
    const groupedEvents: TEvent[][] = Array.from({ length: columnCount }, () => [])

    visibleLayouts.forEach((layout) => {
      const startIndex = Math.max(0, layout.span.startIndex)
      const endIndex = Math.min(columnCount - 1, layout.span.endIndex)

      for (let slotIndex = startIndex; slotIndex <= endIndex; slotIndex += 1) {
        const slotEvents = groupedEvents[slotIndex]
        if (!slotEvents) continue
        slotEvents.push(layout.event)
      }
    })

    return groupedEvents
  }, [columnCount, visibleLayouts])

  const hasPassiveNonSelectableEventsBySlot = useMemo(() => {
    const passiveEventsBySlot = new Set<number>()

    eventsBySlot.forEach((slotEvents, index) => {
      if (slotEvents.some((event) => event.selectable === false)) {
        passiveEventsBySlot.add(index)
      }
    })

    return passiveEventsBySlot
  }, [eventsBySlot])

  const resolveSlotIndex = useCallback(
    (clientX: number) => {
      const node = rowBodyRef.current
      if (!node || columnCount <= 0) return null

      const bounds = node.getBoundingClientRect()
      if (bounds.width <= 0) return null

      const relativeX = clientX - bounds.left
      const slotWidth = bounds.width / columnCount
      const computedIndex = Math.floor(relativeX / slotWidth)
      return Math.max(0, Math.min(columnCount - 1, computedIndex))
    },
    [columnCount]
  )

  const resolveDropTarget = useCallback(
    (clientX: number) => {
      const slotIndex = resolveSlotIndex(clientX)
      if (slotIndex === null) return null
      const slot = slots[slotIndex]
      if (!slot) return null

      return {
        entity: _entity,
        slot,
        events: eventsBySlot[slotIndex] ?? [],
      }
    },
    [_entity, eventsBySlot, resolveSlotIndex, slots]
  )

  const [, drop] = useDrop<TimeGridDragItem, void, unknown>(
    () => ({
      accept: dragAndDropAdapter?.acceptedTypes ?? [],
      canDrop: (item, monitor) => {
        if (!dragAndDropAdapter) return false
        const clientOffset = monitor.getClientOffset()
        if (!clientOffset) return false
        const target = resolveDropTarget(clientOffset.x)
        if (!target) return false

        return dragAndDropAdapter.canDrop?.(item, target) ?? true
      },
      hover: (item, monitor) => {
        if (!dragAndDropAdapter?.onHoverChange) return
        const clientOffset = monitor.getClientOffset()
        const target = clientOffset ? resolveDropTarget(clientOffset.x) : null
        const canDrop = !!(item && target && (dragAndDropAdapter.canDrop?.(item, target) ?? true))

        dragAndDropAdapter.onHoverChange({
          item: item ?? null,
          target,
          isOver: monitor.isOver({ shallow: true }),
          canDrop,
        })
      },
      drop: (item, monitor) => {
        if (!dragAndDropAdapter) return
        const clientOffset = monitor.getClientOffset()
        if (!clientOffset) return
        const target = resolveDropTarget(clientOffset.x)
        if (!target) return
        if (!(dragAndDropAdapter.canDrop?.(item, target) ?? true)) return

        Promise.resolve(dragAndDropAdapter.onDrop(item, target)).catch(() => {})
      },
    }),
    [dragAndDropAdapter, resolveDropTarget]
  )

  const setRowBodyRef = useCallback(
    (node: HTMLDivElement | null) => {
      rowBodyRef.current = node
      if (!dragAndDropAdapter) return
      drop(node)
    },
    [dragAndDropAdapter, drop]
  )
  const handleCellClick = (event: MouseEvent) => {
    if (!onCellSelected) return

    const slotIndex = resolveSlotIndex(event.clientX)
    if (slotIndex === null) return
    if (isCellSelectable && !isCellSelectable(slotIndex)) return

    onCellSelected(slotIndex, {
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
    })
  }

  const handleCellMouseMove = (event: MouseEvent) => {
    const slotIndex = resolveSlotIndex(event.clientX)
    if (slotIndex !== null && isCellSelectable && !isCellSelectable(slotIndex)) {
      if (hoveredSlotIndex !== null) setHoveredSlotIndex(null)
      if (isInBottomPadding) setIsInBottomPadding(false)
      return
    }

    if (slotIndex !== hoveredSlotIndex) {
      setHoveredSlotIndex(slotIndex)
    }

    const node = rowBodyRef.current
    if (node) {
      const bounds = node.getBoundingClientRect()
      const relativeY = event.clientY - bounds.top
      const cellHeight = bounds.height
      const inBottom = relativeY >= cellHeight - CELL_BOTTOM_PADDING_PX
      if (inBottom !== isInBottomPadding) {
        setIsInBottomPadding(inBottom)
      }
    }
  }

  const handleCellMouseLeave = () => {
    setHoveredSlotIndex(null)
    setIsInBottomPadding(false)
    setIsHoveringAddButton(false)
  }

  const handleRowHeaderClick = (event: MouseEvent) => {
    onRowHeaderSelected?.({
      shiftKey: event.shiftKey,
      metaKey: event.metaKey,
      ctrlKey: event.ctrlKey,
    })
  }

  const renderSlotOverlayGrid = ({
    keyPrefix,
    zIndex,
    renderCell,
    className,
  }: {
    keyPrefix: string
    zIndex?: string
    renderCell: (index: number) => ReactNode
    className?: string
  }) => (
    <Box
      aria-hidden="true"
      className={className}
      style={{
        position: 'absolute',
        inset: '0',
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
        pointerEvents: 'none',
        zIndex,
      }}
    >
      {Array.from({ length: columnCount }).map((_, index) => (
        <Box key={`${keyPrefix}-${index}`} style={{ position: 'relative' }}>
          {renderCell(index)}
        </Box>
      ))}
    </Box>
  )
  const handleRowHeaderMouseDown = onHeaderDragStart
    ? (event: MouseEvent) => {
        event.preventDefault()
        onHeaderDragStart({
          axis: 'row',
          index: rowIndex,
          modifiers: {
            shiftKey: event.shiftKey,
            metaKey: event.metaKey,
            ctrlKey: event.ctrlKey,
          },
        })
      }
    : undefined

  return (
    <Box
      role="row"
      ref={rowRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `${rowHeaderWidth}px repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
        minWidth: `${gridMinWidth}px`,
        alignItems: 'stretch',
      }}
    >
      <Box
        role="rowheader"
        background="grey000White"
        onClick={handleRowHeaderClick}
        onMouseEnter={() => setIsRowHeaderHovered(true)}
        onMouseLeave={() => setIsRowHeaderHovered(false)}
        extra={{
          'data-time-grid-row-index': rowIndex,
          onMouseDown: handleRowHeaderMouseDown,
        }}
        className={
          showGridLines
            ? `
            border-solid border-b border-b-f1-border border-r border-f1-border
            ${isRowHeaderHovered && !isRowSelected ? 'border-r-2 border-f1-border-selected' : ''}
            ${isRowSelected ? 'border-r-2 border-f1-border-selected-bold' : ''}`
            : ''
        }
        style={{
          position: 'relative',
          left: '0',
          zIndex: '1',
          display: 'flex',
          gap: '0',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          boxSizing: 'border-box',
          overflow: 'visible',
          userSelect: 'none',
        }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'stretch',
            width: '100%',
            height: '100%',
          }}
        >
          {rowHeader}
        </Box>
        {rowActions ?? null}
      </Box>
      <Box
        role="gridcell"
        onClick={handleCellClick}
        onMouseLeave={handleCellMouseLeave}
        ref={setRowBodyRef}
        extra={{ onMouseMove: handleCellMouseMove }}
        className={showGridLines ? 'border-solid border-b border-b-f1-border' : ''}
        style={{
          gridColumn: `2 / span ${columnCount}`,
          position: 'relative',
          overflow: 'visible',
          userSelect: 'none',
        }}
      >
        {hoveredSlotIndex !== null || (ghostCellSlotIndexes?.size ?? 0) > 0 ? (
          <Box
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: '0',
              display: 'grid',
              gridTemplateColumns: `repeat(${columnCount}, minmax(${minColumnWidth}px, 1fr))`,
              pointerEvents: 'none',
            }}
          >
            {Array.from({ length: columnCount }).map((_, index) => {
              const isHovered = index === hoveredSlotIndex
              const hasGhostCard = ghostCellSlotIndexes?.has(index) ?? false
              const isSelectableCell = isCellSelectable ? isCellSelectable(index) : true
              const isDisabledCell = disabledCellSlotIndexes?.has(index) ?? false
              const isEmptyCell = !occupiedSlots.has(index) && !hasGhostCard
              const isOccupiedCell = occupiedSlots.has(index) && !hasGhostCard
              const showHighlight =
                isHovered && isSelectableCell && !hasGhostCard && !isHoveringAddButton
              const showAddButton =
                isSelectableCell &&
                !isDisabledCell &&
                ((isHovered && isEmptyCell) ||
                  (isHovered && hasGhostCard) ||
                  (isHovered && isOccupiedCell && isInBottomPadding))

              return (
                <Box
                  key={`hover-cell-${index}`}
                  style={{
                    ...(isHovered || hasGhostCard ? { position: 'relative' } : undefined),
                  }}
                >
                  {showHighlight ? (
                    <Box
                      aria-hidden="true"
                      className="bg-f1-background-selected"
                      style={{
                        position: 'absolute',
                        inset: '0',
                        opacity: '0.5',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : null}
                  {showAddButton && onAddShiftClick ? (
                    <Box
                      extra={{
                        onMouseEnter: () => setIsHoveringAddButton(true),
                        onMouseLeave: () => setIsHoveringAddButton(false),
                      }}
                      style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '4px',
                        pointerEvents: 'auto',
                      }}
                    >
                      <F0Button
                        icon={Add}
                        label={i18n.t('shift_management.quick_actions.add_shift')}
                        hideLabel
                        size="sm"
                        variant="outline"
                        onClick={(event: React.MouseEvent) => {
                          event.stopPropagation()
                          const rect = event.currentTarget.getBoundingClientRect()
                          onAddShiftClick(index, rect)
                        }}
                      />
                    </Box>
                  ) : null}
                </Box>
              )
            })}
          </Box>
        ) : null}
        {disabledCellSlotIndexes?.size
          ? renderSlotOverlayGrid({
              keyPrefix: 'disabled-cell',
              renderCell: (index) =>
                disabledCellSlotIndexes.has(index) &&
                !hasPassiveNonSelectableEventsBySlot.has(index) ? (
                  <Box
                    aria-hidden="true"
                    className="bg-f1-background-secondary"
                    style={{
                      position: 'absolute',
                      inset: '0',
                      opacity: '0.8',
                      pointerEvents: 'none',
                      zIndex: '1',
                    }}
                  />
                ) : null,
            })
          : null}
        {selectedCellSlots && selectedCellSlots.size > 0
          ? renderSlotOverlayGrid({
              keyPrefix: 'selected-cell',
              zIndex: '1',
              renderCell: (index) => (selectedCellSlots.has(index) ? <SelectionOverlay /> : null),
            })
          : null}
        {copiedCellSlots && copiedCellSlots.size > 0
          ? renderSlotOverlayGrid({
              keyPrefix: 'copied-cell',
              zIndex: '1',
              renderCell: (index) =>
                copiedCellSlots.has(index) ? <SelectionOverlay dashed /> : null,
            })
          : null}
        {copiedColumnIndexes.size > 0
          ? renderSlotOverlayGrid({
              keyPrefix: 'copied-column',
              zIndex: '1',
              renderCell: (index) =>
                copiedColumnIndexes.has(index) ? <SelectionOverlay dashed /> : null,
            })
          : null}
        {showGridLines
          ? renderSlotOverlayGrid({
              keyPrefix: 'grid-line',
              className: isRowCopied
                ? 'border border-dashed border-f1-border-selected-bold'
                : undefined,
              renderCell: () => (
                <Box
                  style={{ position: 'absolute', inset: '0' }}
                  className="border-solid border-r border-f1-border"
                />
              ),
            })
          : null}
        {secondaryLayer ? (
          <Box
            style={{
              position: 'absolute',
              inset: '0',
              pointerEvents: 'none',
            }}
          >
            {/* Secondary layer is rendered behind events (e.g., background markers). */}
            {secondaryLayer}
          </Box>
        ) : null}
        <TimeGridRowEvents
          entity={_entity}
          layouts={visibleLayouts}
          paddingX={paddingX}
          paddingY={paddingY}
          cardLayout={cardLayout}
          minColumnWidth={minColumnWidth}
          columnCount={columnCount}
          eventRenderer={eventRenderer}
          cardAppearance={cardAppearance}
          dragDropHandlers={dragDropHandlers}
          dragAndDropAdapter={dragAndDropAdapter}
          selectedEventIds={selectedEventIds}
          isSingleSelectedEventGlobally={isSingleSelectedEventGlobally}
          onEventSelected={onEventSelected}
          onCopySelection={onCopySelection}
          ghostCellSlotIndexes={ghostCellSlotIndexes}
          ghostAnchorSlotIndex={ghostAnchorSlotIndex}
          onGhostCardRef={onGhostCardRef}
        />
      </Box>
    </Box>
  )
}

export { TimeGridRow }
